import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  billingCheckoutApi,
  billingCustomerPortalApi,
  confirmCreditPurchaseApi,
  confirmPurchaseAiCreditApi,
  getBillingPlanApi,
  getBillingSessionApi,
  purchaseAiCreditApi,
  purchaseCreditApi,
  getCheckinBusinessBillingSummaryApi,
  getCheckinBusinessBillingTableApi,
  getSubscriptionDetailsApi,
  restoreSubscriptionApi,
  getBillingInvoicesApi,
} from "../../apis";
import {
  IAddonPurchaseAiCredits,
  IBillingCheckoutData,
  IPurchaseCredits,
} from "../../apis/IApisInterface";
import { showSnackbar } from "../../slices/SnackbarSlice";
import { billingUpgradePreviewApi } from "../../apis/UserApis/BillingApi";

export const purchaseCreditAction = createAsyncThunk(
  "purchase/credits",
  async (payload: IPurchaseCredits, { rejectWithValue }) => {
    try {
      const response = await purchaseCreditApi(payload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error || { message: "Could not purchase credits" },
      );
    }
  },
);

export const confirmCreditPurchaseAction = createAsyncThunk(
  "confirm/purchase/credits",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await confirmCreditPurchaseApi(id);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error || { message: "Could not confirm purchase credits" },
      );
    }
  },
);

export const getBillingPlanAction = createAsyncThunk(
  "get/billing/plan",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getBillingPlanApi();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error || { message: "Could not get billing plan" },
      );
    }
  },
);

export const billingCheckoutAction = createAsyncThunk(
  "billing/check-out",
  async (data: IBillingCheckoutData, { rejectWithValue, dispatch }) => {
    try {
      const response = await billingCheckoutApi(data);
      return response.data;
    } catch (error: any) {
      dispatch(
        showSnackbar({
          message: error?.response?.data?.message,
          type: "error",
        }),
      );
      return rejectWithValue(
        error || { message: "Could not get billing plan" },
      );
    }
  },
);

export const getBillingSessionAction = createAsyncThunk(
  "get/billing/session",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await getBillingSessionApi(id);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error || { message: "Could not get billing session" },
      );
    }
  },
);

export const purchaseAiCreditAction = createAsyncThunk(
  "addon/purchase-ai-credits",
  async (data: IAddonPurchaseAiCredits, { rejectWithValue }) => {
    try {
      const response = await purchaseAiCreditApi(data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error || { message: "Could not purchase ai credits" },
      );
    }
  },
);

export const confirmPurchaseAiCreditAction = createAsyncThunk(
  "addon/consfirm-purchase-ai-credits",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await confirmPurchaseAiCreditApi(id);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error || { message: "Could not confirm purchase ai credits" },
      );
    }
  },
);

export const billingCustomerPortalAction = createAsyncThunk(
  "/billing/customer-portal",
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response = await billingCustomerPortalApi();
      dispatch(
        showSnackbar({
          message: response?.data?.message || "keyword added successfully!",
          type: "success",
        }),
      );
      return response.data;
    } catch (error: any) {
      dispatch(
        showSnackbar({
          message: error?.response?.data?.message,
          type: "error",
        }),
      );
      return rejectWithValue(
        error || { message: "Could not get billing customer portal" },
      );
    }
  },
);

export const billingupgradePreviewAction = createAsyncThunk(
  "/billing/preview-upgrade",
  async (
    { planId, type }: { planId: string; type: string },
    { rejectWithValue },
  ) => {
    try {
      const reponse = await billingUpgradePreviewApi(planId, type);
      return reponse.data;
    } catch (error: any) {
      return rejectWithValue(
        error || { message: "Could not billing upgrade preview" },
      );
    }
  },
);

export const getCheckinBusinessBillingSummaryAction = createAsyncThunk(
  "checkin-business/billing-summary",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getCheckinBusinessBillingSummaryApi();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error || { message: "Could not fetch billing summary" },
      );
    }
  },
);

export const getCheckinBusinessBillingTableAction = createAsyncThunk(
  "checkin-business/billing-table",
  async (
    payload: {
      page?: number;
      limit?: number;
      keyword?: string;
      status?: string;
      sort_by?: string;
      sort_order?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await getCheckinBusinessBillingTableApi(payload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error || { message: "Could not fetch billing table" },
      );
    }
  },
);

export const getSubscriptionDetailsAction = createAsyncThunk(
  "billing/subscription-details",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getSubscriptionDetailsApi();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error || { message: "Could not fetch subscription details" },
      );
    }
  },
);

export const restoreSubscriptionAction = createAsyncThunk(
  "billing/restore-subscription",
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response = await restoreSubscriptionApi();
      dispatch(
        showSnackbar({
          message: response?.data?.message || "Subscription restored successfully",
          type: "success",
        }),
      );
      return response.data;
    } catch (error: any) {
      dispatch(
        showSnackbar({
          message: error?.response?.data?.message || "Restore subscription failed",
          type: "error",
        }),
      );
      return rejectWithValue(
        error?.response?.data || { message: "Restore subscription failed" },
      );
    }
  },
);

export const getBillingInvoicesAction = createAsyncThunk(
  "billing/get-invoices",
  async (
    payload: {
      page?: number;
      limit?: number;
      sort_by?: string;
      sort_order?: string;
      category_filter?: string;
      date_from?: string;
      date_to?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await getBillingInvoicesApi(payload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error || { message: "Could not fetch billing invoices" },
      );
    }
  },
);

